import { useEffect, useCallback, useRef, useMemo } from 'react';

// ─── Global Registry ─────────────────────────────────────────────────────────
// Tracks all registered shortcuts for cheatsheet / conflict detection.
const _registry = new Map(); // scope → [{ combo, label, category }]

/** Get all registered shortcuts (for cheatsheet UI) */
export const getShortcutRegistry = () => {
  const all = [];
  for (const [scope, bindings] of _registry) {
    for (const b of bindings) {
      all.push({ ...b, scope });
    }
  }
  return all;
};

/** Detect conflicting shortcuts across scopes */
export const getConflicts = () => {
  const combos = new Map(); // combo string → [{ scope, label }]
  for (const [scope, bindings] of _registry) {
    for (const b of bindings) {
      const key = b.combo;
      if (!combos.has(key)) combos.set(key, []);
      combos.get(key).push({ scope, label: b.label });
    }
  }
  return [...combos.entries()]
    .filter(([, usages]) => usages.length > 1)
    .map(([combo, usages]) => ({ combo, usages }));
};

// ─── Default Shortcuts ───────────────────────────────────────────────────────
const DEFAULT_SHORTCUTS = [
  { combo: 'mod+k',       key: 'k', mod: true,  shift: false, label: 'Open search',    category: 'Navigation', callback: 'onSearch' },
  { combo: 'mod+b',       key: 'b', mod: true,  shift: false, label: 'Toggle sidebar',  category: 'Navigation', callback: 'onToggleSidebar' },
  { combo: 'mod+shift+t', key: 'T', mod: true,  shift: true,  label: 'Toggle theme',    category: 'Appearance', callback: 'onToggleTheme' },
  { combo: 'escape',      key: 'Escape', mod: false, shift: false, label: 'Close / dismiss', category: 'General', callback: 'onEscape' },
  { combo: 'mod+/',       key: '/', mod: true,  shift: false, label: 'Show shortcuts',  category: 'Help',       callback: 'onShowHelp' },
  { combo: 'mod+shift+n', key: 'N', mod: true,  shift: true,  label: 'New item',        category: 'Actions',    callback: 'onNew' },
  { combo: 'mod+s',       key: 's', mod: true,  shift: false, label: 'Save',            category: 'Actions',    callback: 'onSave' },
  { combo: 'mod+shift+d', key: 'D', mod: true,  shift: true,  label: 'Toggle debug',    category: 'Developer',  callback: 'onDebug' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────
const isModKey = (e) => e.ctrlKey || e.metaKey;
const isInputFocused = () => {
  const tag = document.activeElement?.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' ||
    document.activeElement?.isContentEditable;
};

/**
 * Custom hook for global keyboard shortcuts
 *
 * Features:
 * - 8 built-in shortcuts (search, sidebar, theme, escape, help, new, save, debug)
 * - Custom shortcuts via extraShortcuts option
 * - Scope management (only active scope handles keys)
 * - Modal awareness (skips shortcuts when input/textarea focused — except Escape)
 * - Global registry for cheatsheet UI
 * - Conflict detection
 *
 * @param {Object} callbacks — { onSearch, onToggleSidebar, onToggleTheme, onEscape, onShowHelp, onNew, onSave, onDebug }
 * @param {Object} [options]
 * @param {string} [options.scope='global'] — scope name for registry
 * @param {boolean} [options.enabled=true] — enable/disable all shortcuts
 * @param {Array} [options.extraShortcuts] — [{ combo, key, mod, shift, label, category, handler }]
 */
export const useKeyboardShortcuts = (callbacks = {}, options = {}) => {
  const {
    scope = 'global',
    enabled = true,
    extraShortcuts = [],
  } = options;

  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  // Build full shortcut list
  const shortcuts = useMemo(() => {
    const base = DEFAULT_SHORTCUTS.map((s) => ({
      ...s,
      handler: () => callbacksRef.current[s.callback]?.(),
    }));
    const extras = extraShortcuts.map((s) => ({
      ...s,
      handler: s.handler,
    }));
    return [...base, ...extras];
  }, [extraShortcuts]);

  // Register in global registry
  useEffect(() => {
    const entries = shortcuts.map((s) => ({
      combo: s.combo,
      label: s.label,
      category: s.category || 'Custom',
    }));
    _registry.set(scope, entries);
    return () => { _registry.delete(scope); };
  }, [scope, shortcuts]);

  // Key handler
  const handleKeyDown = useCallback((event) => {
    if (!enabled) return;

    for (const shortcut of shortcuts) {
      const modMatch = shortcut.mod ? isModKey(event) : !isModKey(event);
      const shiftMatch = shortcut.shift ? event.shiftKey : !event.shiftKey;
      const keyMatch = event.key === shortcut.key;

      if (modMatch && shiftMatch && keyMatch) {
        // Allow Escape even when input is focused; block others
        if (shortcut.key !== 'Escape' && isInputFocused()) return;

        event.preventDefault();
        shortcut.handler();
        return;
      }
    }
  }, [enabled, shortcuts]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  /**
   * Get cheatsheet data grouped by category
   * @returns {{ category: string, shortcuts: { combo: string, label: string }[] }[] }
   */
  const getCheatsheet = useCallback(() => {
    const grouped = {};
    for (const s of shortcuts) {
      const cat = s.category || 'Other';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push({
        combo: s.combo.replace('mod', navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'),
        label: s.label,
      });
    }
    return Object.entries(grouped).map(([category, items]) => ({ category, shortcuts: items }));
  }, [shortcuts]);

  return { getCheatsheet };
};

export default useKeyboardShortcuts;
